import { useEffect, useMemo } from "react";

import { useLocaisDisponiveis } from "@/modules/carga-de-trabalho";

import {
  formatPeriodo,
  obraLabel,
  obrasDoConflito,
  sameId,
} from "../../resolver-conflito-local.utils";
import type {
  LocaisEstado,
  SelecionarLocalStepProps,
  SelecionarLocalStepViewModel,
} from "./types-selecionar-local-step";

const cidadeUf = (item: { cidade?: string; uf?: string }) =>
  [item.cidade, item.uf].filter(Boolean).join(" - ");

/** Passo 1: obra que terá o Local trocado e o Local substituto (com a disponibilidade no período). */
export function useSelecionarLocalStep({
  local,
  conflito,
  alocacaoId,
  substituto,
  search,
  errors,
  escolherObra,
  escolherSubstituto,
  limparSubstituto,
  buscar,
}: SelecionarLocalStepProps): SelecionarLocalStepViewModel {
  const alocacoes = obrasDoConflito(conflito);
  const alocacao = alocacoes.find((item) => item.id === alocacaoId) ?? null;

  const result = useLocaisDisponiveis({
    dataInicio: alocacao?.inicio ?? conflito.inicio,
    dataFim: alocacao?.fim ?? conflito.fim,
    ignorarObraId: alocacao?.obra.id ?? "",
    busca: search,
  });

  // O próprio Local em conflito não é candidato a substituto.
  const candidatos = useMemo(
    () => result.locais.filter((item) => !sameId(item.id, local.id)),
    [result.locais, local.id],
  );

  // Trocar a obra muda o período: quem ficou indisponível nele perde a seleção.
  useEffect(() => {
    if (!substituto || result.status !== "success") return;
    const atual = candidatos.find((item) => sameId(item.id, substituto.id));
    if (atual?.disponibilidade === "INDISPONIVEL") limparSubstituto();
  }, [candidatos, result.status, substituto, limparSubstituto]);

  let locais: LocaisEstado = { tipo: "carregando" };
  if (result.status === "error") {
    locais = { tipo: "erro" };
  } else if (result.status === "success") {
    locais =
      candidatos.length > 0
        ? {
            tipo: "lista",
            opcoes: candidatos.map((item) => ({
              id: item.id,
              nome: item.nome,
              cidadeUf: cidadeUf(item) || "-",
              indisponivel: item.disponibilidade === "INDISPONIVEL",
              marcado: substituto?.id === item.id,
            })),
          }
        : { tipo: "vazio" };
  }

  return {
    localNome: local.nome,
    localCidadeUf: cidadeUf(local) || "-",
    obras: alocacoes.map((item) => {
      // A API só informa o período alocado da obra no Local; ele também vale para a obra.
      const periodo = formatPeriodo(item.inicio, item.fim);
      return {
        id: item.id,
        titulo: `Obra ${obraLabel(item)}`,
        periodoObra: periodo,
        periodoAlocado: periodo,
        marcada: item.id === alocacaoId,
      };
    }),
    errors,
    search,
    locais,
    escolherObra,
    escolherSubstituto: (localId) => {
      const escolhido = candidatos.find((item) => item.id === localId);
      if (escolhido) escolherSubstituto(escolhido);
    },
    buscar,
  };
}
